import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { ColorNeutralBlack, Spacing12, Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import { ArrowLeft, ArrowRight } from 'autogen/translation-keys/trans-website-imagealttext-icons';
import ScrollSnapSlider, {
    type IOptions,
    PaginationBullets,
    ScrollSnapSliderContext,
    useScrollSnapSlider
} from 'components/generic/ScrollSnapSlider';
import NavigationFadeButton from 'components/generic/ScrollSnapSlider/components/NavigationFadeButton';
import Icon from 'components/Icon';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { TypeCarouselDetailed } from 'types/autogen/contentful';
import { breakpoint, sizes } from 'variables';
import ComponentRenderer from '../../shared/ComponentRenderer';

const Wrapper = styled.div`
    display: block;
    position: relative;
    min-width: 0;
`;

const StyledItem = styled.div`
    display: flex;
    justify-content: center;
    flex: 0 0 100%;
`;

const StyledNavigationButton = styled(NavigationFadeButton)`
    position: absolute;
    padding: 0;
    top: 50%;
    transform: translateY(-50%);

    ${({ navType }) => (navType === 'prev' ? css`left: ${Spacing4};` : css`right: ${Spacing4};`)}
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)`
    gap: ${Spacing8};
    ${invisibleScrollbar()}
`;

const StyledPaginationBullets = styled(PaginationBullets)`
    position: absolute;
    bottom: ${Spacing12};
    left: 50%;
    transform: translateX(-50%);
`;

function Carousel({ fields, sys }: TypeCarouselDetailed) {
    const { content, changeInterval } = fields;
    const entryId = sys.id;

    const inspectorProps = useContentfulInspectorMode({ entryId: entryId });
    const sliderOptions: IOptions = {
        draggable: false,
        loop: false,
        autoplay: changeInterval > 0 ? { intervalDuration: changeInterval * 1000 } : false
    };

    const { ref, slider } = useScrollSnapSlider(sliderOptions);
    const { t } = useTranslation();

    return (
        <Wrapper>
            <ScrollSnapSliderContext.Provider value={slider}>
                <StyledScrollSnapSlider ref={ref} pauseOnHover {...inspectorProps({ fieldId: 'internalName' })}>
                    {content?.map((item, i) => (
                        <StyledItem key={i} data-slide-index={i}>
                            <ComponentRenderer style={{ width: '100%' }} {...item} />
                        </StyledItem>
                    ))}
                </StyledScrollSnapSlider>

                <StyledNavigationButton navType="prev" variant="primaryText" size="sm" aria-label={t(ArrowLeft)}>
                    <Icon type="ChevronLeft" size={16} fill={ColorNeutralBlack} />
                </StyledNavigationButton>

                <StyledNavigationButton navType="next" variant="primaryText" size="sm" aria-label={t(ArrowRight)}>
                    <Icon type="ChevronRight" size={16} fill={ColorNeutralBlack} />
                </StyledNavigationButton>

                <StyledPaginationBullets bulletsCount={content?.length ?? 0} />
            </ScrollSnapSliderContext.Provider>
        </Wrapper>
    );
}

export default withErrorBoundary(Carousel, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
